/**
 * Dialog styles - layout and interactive states.
 * Colors come from MUI theme.
 */

import { SxProps, Theme } from '@mui/material';

export const dialogStyles = {
  // Base dialog styles.
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: 'text.secondary',
    zIndex: 2,
    '&:hover': {
      color: 'text.primary',
      bgcolor: 'custom.dialogHeaderBg',
    },
    cursor: 'pointer',
  } as SxProps<Theme>,
  contentPadding: {
    p: 1,
    flex: 1,
  } as SxProps<Theme>,
  centeredOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
    boxSizing: 'border-box',
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
  } as SxProps<Theme>,
  viewportOverlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
    boxSizing: 'border-box',
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
  } as SxProps<Theme>,
  centeredContent: {
    width: 'min(670px, calc(100% - 32px))',
    height: 'auto',
    maxHeight: 'calc(100% - 32px)',
    boxShadow: 'custom.dropdownShadow',
  } as SxProps<Theme>,

  // Consent dialog styles.
  consentContainer: {
    textAlign: 'center',
    maxWidth: '400px',
    width: '100%',
  } as SxProps<Theme>,
  consentTitle: {
    fontSize: '12px',
    mb: 0.5,
    color: 'primary.main',
  } as SxProps<Theme>,
  consentBodyText: {
    mb: 0.75,
    fontSize: '11px',
    lineHeight: 1.3,
  } as SxProps<Theme>,
  consentCheckboxContainer: {
    mb: 0.75,
    display: 'flex',
    justifyContent: 'center',
  } as SxProps<Theme>,
  consentCheckbox: {
    p: 0.125,
    '& .MuiSvgIcon-root': {
      fontSize: '13px',
    },
  } as SxProps<Theme>,
  consentCheckboxLabel: {
    alignItems: 'center',
    margin: 0,
  } as SxProps<Theme>,
  consentLabelText: {
    fontSize: '9px',
  } as SxProps<Theme>,
  consentButtonContainer: {
    display: 'flex',
    gap: 0.75,
    justifyContent: 'center',
    mt: 0.25,
  } as SxProps<Theme>,
  consentPrimaryButton: {
    fontSize: '10px',
    textTransform: 'uppercase',
    px: 0.5,
    py: 0.25,
    minWidth: '70px',
    minHeight: '24px',
  } as SxProps<Theme>,
  consentSecondaryButton: {
    borderColor: 'divider',
    color: 'text.secondary',
    fontSize: '10px',
    textTransform: 'uppercase',
    px: 0.5,
    py: 0.25,
    minWidth: '50px',
    minHeight: '24px',
    '&:hover': {
      borderColor: 'divider',
      bgcolor: 'action.hover',
    },
  } as SxProps<Theme>,

  // Session discard dialog styles.
  discardContainer: {
    p: 0.25,
  } as SxProps<Theme>,
  discardTitle: {
    fontSize: '20px',
    lineHeight: 1.1,
    textAlign: 'center',
    mb: 0.5,
  } as SxProps<Theme>,
  discardSubtitle: {
    fontSize: '16px',
    lineHeight: 1.2,
    textAlign: 'center',
    mb: 1,
  } as SxProps<Theme>,
  discardWarningText: {
    color: 'warning.main',
    fontSize: '12px',
    lineHeight: 1.1,
    textAlign: 'center',
    fontStyle: 'italic',
    mb: 1,
  } as SxProps<Theme>,
  discardButtonContainer: {
    gap: 0.75,
    justifyContent: 'center',
    pt: 0.25,
  } as SxProps<Theme>,
  discardSmallButton: {
    minWidth: 'auto',
    px: 1.5,
    py: 0.25,
    fontSize: '12px',
  } as SxProps<Theme>,
  discardCancelButton: {
    minWidth: 'auto',
    px: 1.5,
    py: 0.25,
    fontSize: '12px',
    borderColor: 'divider',
    color: 'text.secondary',
    '&:hover': {
      borderColor: 'primary.main',
      backgroundColor: 'action.hover',
    },
  } as SxProps<Theme>,
  discardConfirmButton: {
    minWidth: 'auto',
    px: 1.5,
    py: 0.25,
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: 'warning.main',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'warning.dark',
    },
  } as SxProps<Theme>,

  // Exit confirmation dialog styles.
  exitContainer: {
    p: 0.5,
  } as SxProps<Theme>,
  exitTitle: {
    fontSize: '14px',
    lineHeight: 1.2,
    textAlign: 'center',
    mb: 1,
  } as SxProps<Theme>,
  exitSubtitle: {
    fontSize: '12px',
    lineHeight: 1.3,
    textAlign: 'center',
    mb: 1.5,
  } as SxProps<Theme>,
  exitButtonContainer: {
    gap: 1,
    mt: 1.5,
  } as SxProps<Theme>,
  exitActionButton: {
    fontWeight: 600,
    fontSize: '11px',
  } as SxProps<Theme>,

  // Telehealth instructions dialog styles.
  telehealthDialogContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  } as SxProps<Theme>,
  telehealthPreviewContainer: {
    display: 'flex',
    justifyContent: 'center',
    px: { xs: 1.5, sm: 4 },
    pt: { xs: 1.5, sm: 3 },
    pb: { xs: 1.5, sm: 2.5 },
    backgroundColor: 'custom.backgroundSelected',
    flexShrink: 0,
  } as SxProps<Theme>,
  telehealthPreview: {
    width: 'min(670px, 100%)',
    overflow: 'hidden',
    borderRadius: '8px',
    backgroundColor: 'background.paper',
    border: 2,
    borderColor: 'primary.main',
    boxShadow: 'custom.cardShadow',
  } as SxProps<Theme>,
  telehealthPreviewHeader: {
    display: 'flex',
    gap: 1,
    px: 2.5,
    pt: 2,
    pb: 1,
    backgroundColor: 'background.paper',
    flexDirection: 'column',
    alignItems: 'flex-start',
  } as SxProps<Theme>,
  telehealthPreviewTitle: {
    color: 'text.primary',
    fontSize: { xs: '14px', sm: '17px' },
    fontWeight: 700,
    lineHeight: 1.2,
  } as SxProps<Theme>,
  telehealthPreviewSubtitle: {
    color: 'text.primary',
    fontSize: { xs: '10px', sm: '12px' },
    lineHeight: 1.2,
  } as SxProps<Theme>,
  telehealthPreviewTabs: {
    display: 'flex',
    gap: 3,
    px: 2.5,
    backgroundColor: 'background.paper',
    borderBottom: 1,
    borderColor: 'divider',
  } as SxProps<Theme>,
  telehealthPreviewTab: {
    px: 1,
    py: 1,
    color: 'text.secondary',
    fontSize: { xs: '10px', sm: '12px' },
  } as SxProps<Theme>,
  telehealthPreviewTabActive: {
    px: 1,
    py: 1,
    borderBottom: 2,
    borderColor: 'primary.main',
    backgroundColor: 'background.paper',
    color: 'primary.dark',
    fontSize: { xs: '10px', sm: '12px' },
    fontWeight: 700,
  } as SxProps<Theme>,
  telehealthPreviewBody: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1.5,
    p: 1.5,
    backgroundColor: 'custom.backgroundAlt',
  } as SxProps<Theme>,
  telehealthPreviewTabList: {
    overflow: 'hidden',
    borderRadius: '8px',
    backgroundColor: 'background.paper',
    border: 1,
    borderColor: 'divider',
  } as SxProps<Theme>,
  telehealthPreviewTabListHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: 1.25,
    color: 'text.primary',
    fontSize: { xs: '10px', sm: '12px' },
    fontWeight: 600,
  } as SxProps<Theme>,
  telehealthPreviewSelectedTab: {
    px: 1.5,
    py: 1.25,
    backgroundColor: 'custom.backgroundSelected',
    color: 'text.primary',
    fontSize: { xs: '10px', sm: '12px' },
    borderTop: 1,
    borderBottom: 1,
    borderColor: 'divider',
  } as SxProps<Theme>,
  telehealthPreviewPlaceholder: {
    height: { xs: '18px', sm: '24px' },
    borderBottom: 1,
    borderColor: 'custom.backgroundAlt',
    '&:nth-of-type(even)': {
      backgroundColor: 'custom.backgroundAlt',
    },
  } as SxProps<Theme>,
  telehealthPreviewShareCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: { xs: '160px', sm: '218px' },
    p: 2,
    borderRadius: '8px',
    backgroundColor: 'custom.backgroundSelected',
  } as SxProps<Theme>,
  telehealthPreviewVolumeIcon: {
    color: 'primary.dark',
    fontSize: { xs: '20px', sm: '28px' },
  } as SxProps<Theme>,
  telehealthPreviewShareText: {
    color: 'text.primary',
    fontSize: { xs: '10px', sm: '12px' },
    fontWeight: 600,
  } as SxProps<Theme>,
  telehealthPreviewToggle: {
    width: '38px',
    height: '20px',
    mt: 1.5,
    ml: 'auto',
    borderRadius: '10px',
    backgroundColor: 'primary.main',
    '&::after': {
      content: '""',
      display: 'block',
      width: '16px',
      height: '16px',
      ml: '20px',
      mt: '2px',
      borderRadius: '50%',
      backgroundColor: 'custom.contrastText',
    },
  } as SxProps<Theme>,
  telehealthPreviewFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.25,
    backgroundColor: 'background.paper',
    borderTop: 1,
    borderColor: 'divider',
  } as SxProps<Theme>,
  telehealthDialogBody: {
    px: { xs: 2, sm: 3 },
    pt: { xs: 2, sm: 2.5 },
    pb: { xs: 1.5, sm: 2 },
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  } as SxProps<Theme>,
  telehealthDialogTitle: {
    mb: 1.25,
    fontSize: { xs: '18px', sm: '21px' },
    lineHeight: 1.2,
    color: 'text.primary',
  } as SxProps<Theme>,
  telehealthDialogLead: {
    mb: 1.25,
    fontSize: { xs: '13px', sm: '15px' },
    lineHeight: 1.4,
    fontWeight: 600,
  } as SxProps<Theme>,
  telehealthInstructionList: {
    m: 0,
    pl: 2.5,
    color: 'text.secondary',
    fontSize: { xs: '12px', sm: '14px' },
    lineHeight: 1.7,
    '& li': {
      pl: 0.5,
    },
  } as SxProps<Theme>,
  telehealthAudioNote: {
    mt: 1.5,
    color: 'text.secondary',
    fontSize: { xs: '11px', sm: '13px' },
    lineHeight: 1.45,
  } as SxProps<Theme>,
  telehealthCheckbox: {
    p: 0.5,
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  } as SxProps<Theme>,
  telehealthCheckboxLabel: {
    mt: 1,
    ml: -0.5,
    color: 'text.secondary',
    fontSize: '11px',
  } as SxProps<Theme>,
  telehealthButtonContainer: {
    justifyContent: 'flex-end',
    gap: 1,
    mt: 'auto',
    flexShrink: 0,
  } as SxProps<Theme>,
  telehealthCancelButton: {
    minWidth: '72px',
    color: 'text.primary',
    borderColor: 'text.secondary',
  } as SxProps<Theme>,
  telehealthContinueButton: {
    minWidth: '92px',
  } as SxProps<Theme>,
};
